import { FunctionComponent, useMemo, useState } from 'react';
import { BarTooltipProps, ResponsiveBar } from '@nivo/bar';
import { ChartLegend } from './types';
import { SeriesLayer } from './series-layer';

export interface StackedBarChartDataPoint extends Record<string, number | string> {
  key: string;
}

export interface SeriesDataPoint {
  key: string;
  unit: string;
  label: string;
  /** Whether to create a separate y-axis for this series */
  yAxis?: boolean;
  /** Whether to reverse the axis (e.g., for negative values) */
  reverseAxis?: boolean;
  /** Minimum span for the series (default: 2) */
  minSpan?: number;
  data: { key: string; value: number | null }[];
}

export interface StackedBarChartProps {
  activeSeries?: string[];
  setActiveSeries?: (seriesId: string) => void;
  groupMode?: 'stacked' | 'grouped';
  keys: string[];
  bars: StackedBarChartDataPoint[];
  series: SeriesDataPoint[];
  legendsBar?: ChartLegend[];
  legendsSeries?: ChartLegend[];
  yLabel?: string;
  xLabelRotation?: number;
  getXLabelFormat?: (value: string) => string;
  getColor: (tag: string) => string;
  tooltip?: FunctionComponent<BarTooltipProps<StackedBarChartDataPoint>>;
  children?: React.ReactNode;
}

export function StackedBarChart({
  activeSeries,
  setActiveSeries,
  groupMode = 'stacked',
  keys,
  bars,
  series,
  legendsBar,
  legendsSeries,
  xLabelRotation = 0,
  yLabel,
  getXLabelFormat,
  getColor,
  tooltip,
}: StackedBarChartProps) {
  const seriesLayers = useMemo(() => {
    return series
      .filter((series) => activeSeries?.includes(series.key))
      .map((series) => {
        return SeriesLayer({
          key: series.key,
          series: series.data,
          unit: series.unit,
          label: series.label,
          yAxis: series.yAxis,
          reverseAxis: series.reverseAxis,
          color: getColor(series.key),
          minSpan: series.minSpan ?? 2,
          getXLabelFormat,
        });
      });
  }, [activeSeries, series, getColor, getXLabelFormat]);

  const legendsSeriesDimmed = useMemo(() => {
    return legendsSeries?.map((l) => ({
      ...l,
      color: activeSeries?.includes(l.id) ? l.color : `${l.color}80`,
    }));
  }, [legendsSeries, activeSeries]);

  return (
    <div className="relative w-full">
      <ResponsiveBar
        data={bars}
        keys={keys}
        indexBy="key"
        groupMode={groupMode}
        margin={{ top: 50, right: 50, bottom: 100, left: 55 }}
        padding={0.6}
        colors={(datum) => getColor?.(datum.id as string) ?? '#888'}
        axisBottom={{
          tickSize: 0,
          tickPadding: 10,
          tickRotation: xLabelRotation,
          format: getXLabelFormat ? getXLabelFormat : undefined,
        }}
        axisLeft={{ legend: yLabel, legendPosition: 'middle', legendOffset: -50 }}
        enableGridY={true}
        enableGridX={true}
        labelTextColor="transparent"
        legends={
          legendsBar
            ? [
                {
                  dataFrom: 'indexes',
                  data: legendsBar,
                  anchor: 'bottom-left',
                  direction: 'row',
                  justify: false,
                  translateX: 0,
                  translateY: 80,
                  itemsSpacing: 4,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  symbolSize: 12,
                  symbolShape: 'circle',
                },
                {
                  dataFrom: 'indexes',
                  data: legendsSeriesDimmed,
                  anchor: 'bottom-right',
                  direction: 'row',
                  justify: false,
                  translateX: 0,
                  translateY: 80,
                  itemsSpacing: 4,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  symbolSize: 12,
                  symbolShape: 'circle',
                  onClick: (data) => {
                    setActiveSeries?.(data.id as string);
                  },
                },
              ]
            : []
        }
        tooltip={tooltip ? tooltip : undefined}
        layers={['grid', 'axes', 'bars', 'markers', 'legends', ...seriesLayers]}
        theme={{
          crosshair: {
            line: {
              stroke: '#fff',
              strokeWidth: 1,
              strokeOpacity: 0.2,
            },
          },
          grid: {
            line: {
              stroke: '#444',
            },
          },
          text: {
            fill: 'rgba(255, 255, 255, 1)',
          },
          labels: {
            text: {
              fill: 'rgba(255, 255, 255, 0.4)',
            },
          },
          axis: {
            legend: {
              text: {
                fill: 'rgba(255, 255, 255, 0.4)',
              },
            },
            ticks: {
              line: {
                stroke: 'rgba(255, 255, 255, 1)',
              },
            },
          },
        }}
      />
    </div>
  );
}
